import React from 'react';
import { mapIcon } from '../../utils/mapIcon';
import CategoryNew from '../CategoryNew';
import { useNavigate } from 'react-router-dom';
import TipsAndTricksNew from '../TipsAndTricksNew';
import type { AdviceTheme, AdviceResponse } from '../../types/product';

interface CheckedAnswerEntry {
  subthemes?: Record<string, string>[];
}

interface Props {
  theme: AdviceTheme;
  adviceResponseObject: AdviceResponse;
  checkedAnswers: Record<string, CheckedAnswerEntry>;
  themeImageMap?: Record<string, string>;
}

function ThemeResultNew({ theme, adviceResponseObject, checkedAnswers, themeImageMap = {} }: Props) {
  const filteredAnswers = Object.keys(checkedAnswers)
    .filter((key) => key === theme.theme)
    .reduce<Record<string, CheckedAnswerEntry>>((obj, key) => {
      obj[key] = checkedAnswers[key];
      return obj;
    }, {});
  const navigate = useNavigate();
  const navigateToProductsPage = (products: unknown) => {
    navigate('/products', { state: { products } });
  };
  return (
    <div className="flex flex-col gap-4 p-4 lg:p-10 bg-white mt-6 lg:mt-5 rounded-[20px]">
      <div className="inline-flex items-center gap-4">
        <img
          src={'/assets/images/' + (themeImageMap[theme.theme] || mapIcon(theme.theme)) + '.svg'}
          alt={theme.theme + ' icon'}
          className="h-10 w-10"
        />
        <span className="text-2xl lg:text-3xl font-semibold">
          {theme.theme}
        </span>
      </div>
      <div className="text-lg font-bold flex flex-col flex-wrap gap-y-1">
        {Object.values(filteredAnswers).map((obj, index) => {
          const grouped: Record<string, { label: string | null; subitems: string[] }> = {};

          (obj.subthemes || []).forEach((entry) => {
            const [key, rawValue] = Object.entries(entry)[0];
            const value = (rawValue ?? '').toString().trim();
            if (!value) return;

            const parentKey = (key || '').toString().slice(0, 3);

            if (!grouped[parentKey]) {
              grouped[parentKey] = { label: null, subitems: [] };
            }

            if (key.length === 3) {
              grouped[parentKey].label = value;
            } else {
              grouped[parentKey].subitems.push(value);
            }
          });

          return Object.entries(grouped).map(([key, group]) => {
            const items = group.subitems.filter((v) => !!(v && v.trim()));
            const hasLabel = !!(group.label && group.label.trim());
            if (!hasLabel && items.length === 0) return null;

            return (
              <div key={`${index}-${key}`}>
                {hasLabel && (
                  <b className="text-lg font-bold">
                    {group.label}
                    {items.length > 0 ? ':' : '.'}
                  </b>
                )}
                {items.length > 0 && (
                  <span>
                    {items.map((item, subIndex) => (
                      <span key={`${key}-sub-${subIndex}`} className="font-light ml-1">
                        {item.toLowerCase()}
                        {subIndex < items.length - 1 ? ',' : '.'}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            );
          });
        })}
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {theme.advice.productCategories.length === 0 ? (
          <div className="col-span-full text-center text-gray-500 py-8">
            Er zijn momenteel geen producten beschikbaar voor dit thema.
          </div>
        ) : (
          theme.advice.productCategories.map((category) => (
            <CategoryNew
              key={category.id}
              category={category}
              allowedProducts={theme.advice.products}
              adviceResponseObject={adviceResponseObject}
              onClick={() =>
                navigateToProductsPage({
                  category,
                  allowedProducts: theme.advice.products,
                  adviceResponseObject,
                })
              }
            />
          ))
        )}
      </div>
      {theme.advice.content?.length > 0 && (
        <p className="text-xl font-semibold">
          <b>Andere tips</b>
        </p>
      )}
      <TipsAndTricksNew
        content={theme.advice.content}
        adviceResponseObject={adviceResponseObject}
        hasTitle={false}
      />
    </div>
  );
}

export default ThemeResultNew;
